import queryString from 'query-string';
import { PAY_TYPE } from '@os-web/const';
import http from '@os-web/http';

import { isApp, openAppWebview } from './app.ts';
import { parseUA, sleep } from './common.ts';

type OrderInfo = {
  status?: boolean | number;
  pay_url?: string;
  alipay_pay_url?: string;
  wechat_pay_url?: string;
  paypal?: string;
  stripe?: string;
  lark?: string;
  google_pay_url?: string;
};

type JumpPayUrlOptions = {
  target?: '_self' | '_blank';
  windowRef?: Window | null;
  features?: string;
};

export async function getOderInfo(orderId: string) {
  const res = (await http.post('/api/order/list', {
    order_ids: orderId,
  })) as unknown as { list?: OrderInfo[] };

  const list = res?.list;
  if (!Array.isArray(list)) return null;

  return list[0] || null;
}

export async function checkOrderStatus(
  orderId: string,
  { retry = 10, ms = 3000, needOrderInfo = false } = {},
): Promise<boolean | OrderInfo | null> {
  try {
    const orderInfo = await getOderInfo(orderId);

    if (!orderInfo) {
      await Promise.reject('没有获取到订单内容');
    }
    if (needOrderInfo && orderInfo?.status) return orderInfo;

    if (orderInfo?.status) return true;
  } catch (error) {
    console.error(error);
  }

  if (retry <= 0) {
    return false;
  }

  await sleep(ms);
  return checkOrderStatus(orderId, {
    retry: retry - 1,
    ms,
  });
}

export async function miniProgramPay({
  pay_params = '',
  order_id = '',
  return_url = '',
  other_params = {},
  redirect = false,
}: {
  pay_params?: string;
  order_id?: string;
  return_url?: string;
  other_params?: Record<string, unknown>;
  redirect?: boolean;
} = {}) {
  const query: queryString.StringifiableRecord = {
    payParams: pay_params,
    orderId: order_id,
    ...other_params,
  };

  if (return_url) {
    const { url, query: returnQuery } = queryString.parseUrl(return_url);
    delete returnQuery.order_id;
    query.return_url = url;

    Object.keys(returnQuery).forEach((key) => {
      const value = returnQuery[key];
      query[`_${key}`] = encodeURIComponent(String(value));
    });
  }

  const url = queryString.stringifyUrl({
    url: '/packageE/pages/pay/wxPay',
    query,
  });

  const wx = window.wx;
  wx?.miniProgram?.[redirect ? 'redirectTo' : 'navigateTo']?.({
    url,
  });
}

export function getPayUrl(data: OrderInfo, payType: string) {
  let field = payType;

  if (payType === PAY_TYPE.WECHAT) {
    field = parseUA().weixin ? 'wechat_pay_url' : 'pay_url';
  }

  const url = data[field as keyof OrderInfo];
  return typeof url === 'string' ? url : undefined;
}

export async function payByOrderId(
  orderId: string,
  payType: string,
  options: JumpPayUrlOptions = {},
) {
  const payInfo = (await http.get('/api/comm/pay/url', {
    order_id: orderId,
  })) as OrderInfo;

  return jumpPayUrl(payInfo, payType, options);
}

export function jumpPayUrl(data: OrderInfo, payType: string, options: JumpPayUrlOptions = {}) {
  const url = getPayUrl(data, payType);

  if (!url) {
    return 'success';
  }

  if (isApp) {
    openAppWebview(url);
    return;
  }

  if (options.windowRef) {
    options.windowRef.opener = null;
    options.windowRef.location.href = url;
    return;
  }

  if (options.target === '_blank') {
    const openedWindow = window.open(url, '_blank', options.features || 'noopener,noreferrer');
    if (openedWindow) {
      openedWindow.opener = null;
    }
    return;
  }

  location.href = url;
}
